import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

export default function SimilarsPage() {
  const [threshold, setThreshold] = useState(5);
  const [data, setData] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch(`${API}/similars?threshold=${threshold}`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(setData).catch((e) => setErr(String(e.message || e)));
  }, [threshold]);
  useEffect(() => { load(); }, [load]);

  const archive = async (id: string, undo = false) => {
    try {
      const res = await fetch(`${API}/blends/${id}/${undo ? "unarchive" : "archive"}`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      toast.success(undo ? "رجعت من الأرشيف" : "اتأرشفت (قابلة للرجوع)");
      setConfirming(null);
      load();
    } catch (e: any) { toast.error(String(e.message || e)); }
  };

  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="similars-error">{err}</div>;
  if (!data) return <div className="bl-sub py-8 text-center" data-testid="similars-loading">جارٍ التحليل…</div>;

  return (
    <div className="max-w-3xl space-y-3" data-testid="similars-page">
      <div className="bl-card">
        <div className="bl-eyebrow mb-2">🔎 متشابهات — مراجعة (Archive فقط، مفيش حذف نهائي)</div>
        <div className="flex items-center gap-2">
          <span className="bl-sub">العتبة</span>
          <input type="range" min={3} max={9} value={threshold} style={{ accentColor: "var(--bl-gold)" }}
            onChange={(e) => setThreshold(Number(e.target.value))} data-testid="similars-threshold-slider" />
          <span className="bl-pill">{threshold}</span>
          <span className="bl-sub mr-auto" data-testid="similars-count">{data.flagged.length} زوج مرشّح · {data.excludedVariants.length} نسخة نوع مستبعدة (♂/♀)</span>
        </div>
      </div>

      {data.flagged.map((p: any, i: number) => {
        const weaker = p.weakerId === p.a.id ? p.a : p.b;
        return (
          <div key={i} className="bl-card" data-testid={`similar-pair-${p.a.catalog_code}-${p.b.catalog_code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="bl-pill">score {p.score}</span>
              {[p.a, p.b].map((x: any) => (
                <span key={x.id} className={`bl-pill ${x.id === p.weakerId ? "danger" : ""}`}>
                  {x.catalog_code} {x.name} · {x.role_tag}{x.id === p.weakerId ? " (الأضعف)" : ""}{x.archived ? " · مؤرشفة" : ""}
                </span>
              ))}
            </div>
            <div className="bl-sub bl-en mt-1">زيوت مشتركة: {p.sharedOils.join(" · ")} {p.sameFamily ? "· نفس العائلة" : ""}</div>
            <div className="flex gap-2 mt-2">
              {weaker.archived ? (
                <button className="bl-btn ghost" onClick={() => archive(weaker.id, true)} data-testid={`similars-unarchive-${weaker.catalog_code}`}>↩︎ رجّع {weaker.name}</button>
              ) : confirming === `${i}` ? (
                <>
                  <button className="bl-btn danger" onClick={() => archive(weaker.id)} data-testid={`similars-confirm-archive-${weaker.catalog_code}`}>تأكيد أرشفة {weaker.name}</button>
                  <button className="bl-btn ghost" onClick={() => setConfirming(null)}>إلغاء</button>
                </>
              ) : (
                <button className="bl-btn ghost" onClick={() => setConfirming(`${i}`)} data-testid={`similars-archive-${weaker.catalog_code}`}>🗄 أرشف الأضعف ({weaker.name})</button>
              )}
            </div>
          </div>
        );
      })}
      {!data.flagged.length && <div className="bl-card bl-sub text-center py-6" data-testid="similars-empty">مفيش متشابهات فوق العتبة دي</div>}
      <p className="bl-disclaimer">{data.note}</p>
    </div>
  );
}
